import React, { useEffect, useState, useRef } from 'react';
import { Upload, X } from 'lucide-react';
import { AuthState } from '../types';
import { apiClient, menuApi } from '../api/client';

interface KioskTheme { primaryColor: string; mode: 'light' | 'dark' }
interface Settings { displayName: string; kioskTheme: KioskTheme; welcomeBackgroundImage?: string }

const PRESET_COLORS = [
  { label: 'Orange',  value: '#FF6B35' },
  { label: 'Indigo',  value: '#6366F1' },
  { label: 'Emerald', value: '#10B981' },
  { label: 'Rose',    value: '#F43F5E' },
  { label: 'Sky',     value: '#0EA5E9' },
  { label: 'Amber',   value: '#F59E0B' },
  { label: 'Purple',  value: '#A855F7' },
  { label: 'Teal',    value: '#14B8A6' },
];

interface Props { auth: AuthState }

export const SettingsPage: React.FC<Props> = ({ auth }) => {
  const [displayName, setDisplayName] = useState('');
  const [mode, setMode] = useState<'light' | 'dark'>('light');
  const [primaryColor, setPrimaryColor] = useState('#FF6B35');
  const [welcomeBgImage, setWelcomeBgImage] = useState('');
  const [uploadingBg, setUploadingBg] = useState(false);
  const bgInputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Load current settings
  useEffect(() => {
    void (async () => {
      try {
        const { data } = await apiClient.get('/restaurant', {
          params: { restaurantId: auth.restaurantId },
        });
        const s = data.data as Settings;
        setDisplayName(s.displayName || auth.restaurantName);
        setMode(s.kioskTheme?.mode ?? 'light');
        setPrimaryColor(s.kioskTheme?.primaryColor ?? '#FF6B35');
        setWelcomeBgImage(s.welcomeBackgroundImage ?? '');
      } catch {
        setDisplayName(auth.restaurantName);
      } finally {
        setLoading(false);
      }
    })();
  }, [auth]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiClient.put('/restaurant', {
        restaurantId: auth.restaurantId,
        displayName,
        kioskTheme: { primaryColor, mode },
        welcomeBackgroundImage: welcomeBgImage,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      console.error('Failed to save settings', e);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const preview = {
    bg: mode === 'dark' ? '#080810' : `color-mix(in srgb, ${primaryColor} 8%, white)`,
    card: mode === 'dark' ? '#10101e' : '#ffffff',
    text: mode === 'dark' ? '#ffffff' : '#1C1C1E',
  };

  return (
    <div className="min-h-full bg-gray-950 text-white p-6 md:p-10">
      <div className="max-w-2xl mx-auto space-y-8">

        {/* Header */}
        <div>
          <h1 className="text-2xl font-black text-white mb-1">Kiosk Settings</h1>
          <p className="text-gray-500 text-sm">Customize the appearance of your kiosk ordering screen.</p>
        </div>

        {/* Restaurant Name */}
        <section className="bg-gray-900 rounded-2xl p-6 space-y-3">
          <h2 className="font-bold text-sm text-gray-400 uppercase tracking-widest">Display Name</h2>
          <p className="text-gray-500 text-xs">Shown on the kiosk welcome screen.</p>
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Your Restaurant Name"
            className="w-full bg-gray-800 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-indigo-500 text-base"
          />
        </section>

        {/* Color */}
        <section className="bg-gray-900 rounded-2xl p-6 space-y-4">
          <h2 className="font-bold text-sm text-gray-400 uppercase tracking-widest">Primary Color</h2>
          <p className="text-gray-500 text-xs">Used for buttons, category tabs, cart bar, and price badges.</p>

          {/* Preset swatches */}
          <div className="flex flex-wrap gap-3">
            {PRESET_COLORS.map((c) => (
              <button
                key={c.value}
                onClick={() => setPrimaryColor(c.value)}
                title={c.label}
                className="relative w-10 h-10 rounded-full transition-transform hover:scale-110 active:scale-95"
                style={{ backgroundColor: c.value }}
              >
                {primaryColor === c.value && (
                  <span className="absolute inset-0 flex items-center justify-center text-white font-black text-lg">✓</span>
                )}
              </button>
            ))}
          </div>

          {/* Custom picker */}
          <div className="flex items-center gap-4 pt-1">
            <label className="text-sm text-gray-400 font-medium">Custom color</label>
            <div className="relative">
              <input
                type="color"
                value={primaryColor}
                onChange={(e) => setPrimaryColor(e.target.value)}
                className="w-12 h-10 rounded-xl cursor-pointer border-0 bg-transparent"
              />
            </div>
            <span className="text-sm font-mono text-gray-400">{primaryColor.toUpperCase()}</span>
          </div>
        </section>

        {/* Welcome Background Image */}
        <section className="bg-gray-900 rounded-2xl p-6 space-y-4">
          <h2 className="font-bold text-sm text-gray-400 uppercase tracking-widest">Welcome Background</h2>
          <p className="text-gray-500 text-xs">Background image for the kiosk welcome screen. A default image is used if none is uploaded.</p>
          <input
            ref={bgInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              setUploadingBg(true);
              try {
                const { uploadUrl, imageUrl } = await menuApi.getUploadUrl(auth.restaurantId, file.name, file.type);
                await menuApi.uploadImageToS3(uploadUrl, file);
                setWelcomeBgImage(imageUrl);
              } catch (err) {
                console.error('Background upload failed', err);
              } finally {
                setUploadingBg(false);
                if (bgInputRef.current) bgInputRef.current.value = '';
              }
            }}
          />
          {welcomeBgImage ? (
            <div className="relative rounded-xl overflow-hidden">
              <img src={welcomeBgImage} alt="Welcome background" className="w-full h-40 object-cover rounded-xl" />
              <button
                onClick={() => setWelcomeBgImage('')}
                className="absolute top-2 right-2 p-1.5 rounded-full bg-black/60 hover:bg-black/80 text-white transition-colors"
              >
                <X size={16} />
              </button>
            </div>
          ) : (
            <div className="relative rounded-xl overflow-hidden">
              <img src="/kiosk_placeholder.png" alt="Default background" className="w-full h-40 object-cover rounded-xl opacity-50" />
              <span className="absolute inset-0 flex items-center justify-center text-gray-400 text-xs font-bold">DEFAULT IMAGE</span>
            </div>
          )}
          <button
            onClick={() => bgInputRef.current?.click()}
            disabled={uploadingBg}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold bg-gray-800 text-gray-300 hover:text-white hover:bg-gray-700 transition-colors disabled:opacity-50"
          >
            <Upload size={16} />
            {uploadingBg ? 'Uploading...' : 'Upload Image'}
          </button>
        </section>

        {/* Mode */}
        <section className="bg-gray-900 rounded-2xl p-6 space-y-4">
          <h2 className="font-bold text-sm text-gray-400 uppercase tracking-widest">Display Mode</h2>
          <div className="flex gap-3">
            {(['light', 'dark'] as const).map((m) => (
              <button
                key={m}
                onClick={() => setMode(m)}
                className={`flex-1 py-3 rounded-xl text-sm font-bold transition-all ${
                  mode === m
                    ? 'text-white shadow-lg'
                    : 'bg-gray-800 text-gray-400 hover:text-white'
                }`}
                style={mode === m ? { backgroundColor: primaryColor } : {}}
              >
                {m === 'light' ? '☀️ Light' : '🌙 Dark'}
              </button>
            ))}
          </div>
        </section>

        {/* Live preview */}
        <section className="bg-gray-900 rounded-2xl p-6 space-y-3">
          <h2 className="font-bold text-sm text-gray-400 uppercase tracking-widest">Preview</h2>
          <div
            className="rounded-xl p-4 transition-all duration-300"
            style={{ backgroundColor: preview.bg }}
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-lg font-black"
                style={{ backgroundColor: primaryColor }}>
                {displayName.charAt(0).toUpperCase() || 'R'}
              </div>
              <div>
                <p className="font-black text-sm" style={{ color: preview.text }}>{displayName || 'Your Restaurant'}</p>
                <p className="text-xs" style={{ color: '#8E8E93' }}>Touch to start your order</p>
              </div>
            </div>
            <div className="flex gap-2">
              {['Burgers', 'Drinks', 'Sides'].map((cat) => (
                <div key={cat}
                  className="px-3 py-1.5 rounded-full text-xs font-bold"
                  style={{ backgroundColor: primaryColor, color: '#fff' }}>
                  {cat}
                </div>
              ))}
            </div>
            <div className="mt-3 flex gap-2">
              <div className="rounded-xl p-3 flex-1" style={{ backgroundColor: preview.card }}>
                <div className="h-12 rounded-lg mb-2" style={{ backgroundColor: `${primaryColor}22` }} />
                <p className="text-xs font-bold" style={{ color: preview.text }}>Classic Burger</p>
                <p className="text-xs font-black mt-1" style={{ color: primaryColor }}>$12.99</p>
              </div>
              <div className="rounded-xl p-3 flex-1" style={{ backgroundColor: preview.card }}>
                <div className="h-12 rounded-lg mb-2" style={{ backgroundColor: `${primaryColor}22` }} />
                <p className="text-xs font-bold" style={{ color: preview.text }}>Cheese Fries</p>
                <p className="text-xs font-black mt-1" style={{ color: primaryColor }}>$5.99</p>
              </div>
            </div>
          </div>
        </section>

        {/* Save */}
        <div className="flex items-center gap-4">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-8 py-3 rounded-xl text-white font-black text-sm transition-all disabled:opacity-50 hover:brightness-110 active:scale-95"
            style={{ backgroundColor: primaryColor }}
          >
            {saving ? 'Saving…' : saved ? '✓ Saved!' : 'Save Settings'}
          </button>
          {saved && <span className="text-green-400 text-sm font-semibold">Settings saved — reload the kiosk app to apply.</span>}
        </div>
      </div>
    </div>
  );
};
